import type { UserProfile } from "@crialume/sdk";

/**
 * Onde a sessão é persistida. Trocável (ex.: cookie httpOnly + memória) sem
 * tocar no AuthService. No CadFrn as chaves `erp_token`/`erp_user` eram lidas
 * diretamente em pelo menos 4 arquivos.
 */
export interface TokenStorage {
  getToken(): string | null;
  getUser(): UserProfile | null;
  save(token: string, user: UserProfile): void;
  clear(): void;
  /** Mudanças feitas por OUTRA aba. */
  onExternalChange?(listener: () => void): () => void;
}

export function createLocalTokenStorage(prefix = "crialume"): TokenStorage {
  const tokenKey = `${prefix}:token`;
  const userKey = `${prefix}:user`;

  const safe = <T>(fn: () => T, fallback: T): T => {
    try {
      return fn();
    } catch {
      return fallback;
    }
  };

  return {
    getToken: () => safe(() => localStorage.getItem(tokenKey), null),
    getUser: () =>
      safe(() => {
        const raw = localStorage.getItem(userKey);
        return raw ? (JSON.parse(raw) as UserProfile) : null;
      }, null),
    save: (token, user) =>
      safe(() => {
        localStorage.setItem(tokenKey, token);
        localStorage.setItem(userKey, JSON.stringify(user));
      }, undefined),
    clear: () =>
      safe(() => {
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(userKey);
      }, undefined),
    onExternalChange: (listener) => {
      const handler = (event: StorageEvent) => {
        if (event.key === null || event.key === tokenKey) listener();
      };
      window.addEventListener("storage", handler);
      return () => window.removeEventListener("storage", handler);
    },
  };
}

/**
 * Token só em memória (nada em localStorage — o backend do Crialume exige:
 * o refresh token fica em cookie HttpOnly e recria a sessão no boot).
 *
 * Com `channel`, abas do mesmo navegador compartilham login, renovação e
 * logout por `BroadcastChannel`, sem gravar o token em disco.
 */
export function createMemoryTokenStorage(options: { channel?: string } = {}): TokenStorage {
  let token: string | null = null;
  let user: UserProfile | null = null;
  const listeners = new Set<() => void>();
  const channel =
    options.channel && typeof BroadcastChannel !== "undefined"
      ? new BroadcastChannel(options.channel)
      : null;

  type Message = { type: "save"; token: string; user: UserProfile } | { type: "clear" };
  channel?.addEventListener("message", (event: MessageEvent<Message>) => {
    const message = event.data;
    if (message.type === "save") {
      token = message.token;
      user = message.user;
    } else {
      token = null;
      user = null;
    }
    for (const listener of [...listeners]) listener();
  });

  return {
    getToken: () => token,
    getUser: () => user,
    save: (t, u) => {
      token = t;
      user = u;
      channel?.postMessage({ type: "save", token: t, user: u } satisfies Message);
    },
    clear: () => {
      const had = token !== null;
      token = null;
      user = null;
      if (had) channel?.postMessage({ type: "clear" } satisfies Message);
    },
    onExternalChange: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
